import styles from './TextComponent.module.scss';
import { Container } from 'ui/components/4-habitats/Container/Container';
import { Heading } from 'ui/components/1-atoms/Heading/Heading';
import { TopLevelGrid } from 'ui/components/4-habitats/TopLevelGrid/TopLevelGrid';
import { TopLevelGridCell } from 'ui/components/4-habitats/TopLevelGrid/TopLevelGridCell';
import { Kicker, KickerProps } from 'ui/components/1-atoms/Kicker/Kicker';
import { RichText } from 'ui/components/1-atoms/RichText/RichText';
import ColorOptions from 'helpers/types';
import classNames from 'classnames';
import { InfoBox, InfoBoxProps } from 'ui/components/2-molecules/InfoBox/InfoBox';

export interface TextComponentProps {
	bgColor?: ColorOptions;
	heading?: string;
	kicker?: KickerProps;
	text?: string;
	readMoreText?: string;
	readMoreButtonText?: string;
	infoBox?: InfoBoxProps;
	infoBoxAlignment?: 'text' | 'heading';
}

export const TextComponent: React.FC<TextComponentProps> = ({
	heading,
	kicker,
	text,
	readMoreText,
	readMoreButtonText,
	bgColor = ColorOptions.White,
	infoBox,
	infoBoxAlignment = 'text',
}) => {
	const hasKicker = Boolean(kicker);

	const resolvedInfoBoxColor = (() => {
		if (!infoBox) return undefined;
		if (infoBox.color) return infoBox.color;

		const lightBackgrounds = [
			ColorOptions.White,
			ColorOptions.WhiteGrey,
			ColorOptions.LightGrey,
			ColorOptions.LightBlue,
		];

		if (lightBackgrounds.includes(bgColor)) {
			return ColorOptions.Blue;
		}

		return bgColor;
	})();

	const resolvedInfoBoxAccentColor = (() => {
		if (!infoBox) return undefined;

		const lightBackgrounds = [
			ColorOptions.White,
			ColorOptions.WhiteGrey,
			ColorOptions.LightGrey,
			ColorOptions.LightBlue,
		];

		if (lightBackgrounds.includes(bgColor)) {
			return bgColor;
		}

		const darkBackgrounds = [
			ColorOptions.Blue,
			ColorOptions.Green,
			ColorOptions.Red,
			ColorOptions.Violet,
		];

		if (darkBackgrounds.includes(bgColor)) {
			return 'light';
		}

		return undefined;
	})();

	const renderInfoBox = () =>
		infoBox ? (
			<InfoBox
				content={infoBox.content}
				color={resolvedInfoBoxColor}
				accentColor={resolvedInfoBoxAccentColor}
				variant={infoBox.variant}
				className={styles.TextComponent_infoBox}
			/>
		) : null;

	if (!infoBox) {
		return (
			<div className={classNames(styles.TextComponent, styles[`u-color___${bgColor}`])}>
				<Container width="Standard">
					<TopLevelGrid wrap={true}>
						{hasKicker && (
							<TopLevelGridCell desktopWidth="7col" mobileWidth="100">
								<Kicker {...kicker} className={styles.TextComponent_kicker} />
							</TopLevelGridCell>
						)}

						<TopLevelGridCell
							desktopWidth="17col"
							desktopOffset={hasKicker ? null : '7col'}
							mobileWidth="100"
						>
							<Heading headingLevel="h2" style="xl" className={styles.TextComponent_heading}>
								{heading}
							</Heading>

							<RichText
								readMoreText={readMoreText}
								readMoreButtonText={readMoreButtonText}
								content={text}
								className={styles.TextComponent_content}
							/>
						</TopLevelGridCell>
					</TopLevelGrid>
				</Container>
			</div>
		);
	}

	const alignToHeading = infoBoxAlignment === 'heading' && !hasKicker;

	return (
		<div className={classNames(styles.TextComponent, styles[`u-color___${bgColor}`])}>
			<Container width="Standard">
				{alignToHeading ? (
					<>
						<TopLevelGrid wrap={true} className={styles.TextComponent_gridRow}>
							<TopLevelGridCell desktopWidth="7col" mobileWidth="100">
								{renderInfoBox()}
							</TopLevelGridCell>

							<TopLevelGridCell desktopWidth="17col" mobileWidth="100">
								<Heading headingLevel="h2" style="xl" className={styles.TextComponent_heading}>
									{heading}
								</Heading>
							</TopLevelGridCell>
						</TopLevelGrid>

						<TopLevelGrid wrap={true}>
							<TopLevelGridCell desktopWidth="17col" desktopOffset="7col" mobileWidth="100">
								<RichText
									readMoreText={readMoreText}
									readMoreButtonText={readMoreButtonText}
									content={text}
									className={classNames(
										styles.TextComponent_content,
										styles.TextComponent_content___withInfoBox
									)}
								/>
							</TopLevelGridCell>
						</TopLevelGrid>
					</>
				) : (
					<>
						<TopLevelGrid wrap={true} className={styles.TextComponent_gridRow}>
							{hasKicker && (
								<TopLevelGridCell desktopWidth="7col" mobileWidth="100">
									<Kicker {...kicker} className={styles.TextComponent_kicker} />
								</TopLevelGridCell>
							)}

							<TopLevelGridCell
								desktopWidth="17col"
								desktopOffset={hasKicker ? null : '7col'}
								mobileWidth="100"
							>
								<Heading headingLevel="h2" style="xl" className={styles.TextComponent_heading}>
									{heading}
								</Heading>
							</TopLevelGridCell>
						</TopLevelGrid>

						<TopLevelGrid wrap={true}>
							<TopLevelGridCell
								desktopWidth="7col"
								mobileWidth="100"
								className={styles.TextComponent_infoBoxCell}
							>
								{renderInfoBox()}
							</TopLevelGridCell>

							<TopLevelGridCell
								desktopWidth="17col"
								mobileWidth="100"
								className={styles.TextComponent_contentCell___withInfoBox}
							>
								<RichText
									readMoreText={readMoreText}
									readMoreButtonText={readMoreButtonText}
									content={text}
									className={classNames(
										styles.TextComponent_content,
										styles.TextComponent_content___withInfoBox
									)}
								/>
							</TopLevelGridCell>
						</TopLevelGrid>
					</>
				)}
			</Container>
		</div>
	);
};